import { getCurrentAuthUser } from "../repositories/authRepository";
import * as matchRepository from "../repositories/matchRepository";
import * as liveMatchRepository from "../repositories/liveMatchRepository";
import * as matchAccessRepository from "../repositories/matchAccessRepository";
import {
  buildLiveMatchDocument,
  mapLiveStatusToMatchStatus,
  matchMetaToLegacyShape,
  patchMatchFields,
  pickPlayerIds,
} from "./session/matchDocumentHelpers";
import { deleteField } from "firebase/firestore";
import { resolveLiveDocumentId } from "../domain/roomGovernance/liveMatchBinding";
import { fetchRoomMatch } from "../repositories/roomMatchRepository";
import { persistRegistryState } from "../features/registry/repository/registryRepository";
import { tryMigrateMatchCredentials } from "./session/matchAccessMigration";
import { verifyRoomJoin } from "./session/matchJoin";
import { trackRoomCreated } from "./analytics/bridges/roomAnalytics";
import {
  batchCreateMatchWithAccess,
  isPermissionDenied,
} from "./session/createSession";
import {
  initMatchSubscriptionHub,
  subscribeToMatchDocument,
} from "./session/matchSubscriptionHub";
import {
  reportSnapshotMetadata,
  setActiveSyncRoom,
} from "./firebase/firestoreSyncService";
import {
  tryImportLegacyHistory,
  tryImportLegacyLive,
  tryImportLegacyMeta,
  tryImportLegacyPlayers,
  legacyPlayersToRegistryEntries,
  tryImportLegacyTeams,
} from "./session/legacySessionImport";
import { ensureRoomSessionCompetition } from "./cmoe/competitionEnsureService";
import {
  syncRegistryToParticipants,
  syncRoomSessionGrants,
} from "./cmoe/roomSessionDualWrite";
import { isActivityRuntimePrimary } from "../config/cmoeOrchestrationMode";
import {
  applyActivityLiveState,
  subscribeActivityRuntime,
} from "./cmoe/activity/activityRuntimeService";
import { normalizeSportId, DEFAULT_SPORT } from "../domain/sport/sportTypes";

const PLACEHOLDER = "unassigned";

function ownerUid(explicit) {
  return explicit || getCurrentAuthUser()?.uid || null;
}

async function importLegacySession(matchId) {
  const legacyMeta = await tryImportLegacyMeta(matchId);
  if (!legacyMeta) return null;

  const uid = legacyMeta.ownerUid || ownerUid();
  if (!uid) return null;

  const legacyPlayers = await tryImportLegacyPlayers(matchId);
  const timestamps = matchRepository.newMatchTimestamps();
  const matchPayload = {
    createdBy: uid,
    player1Id: PLACEHOLDER,
    player2Id: PLACEHOLDER,
    status: "pending",
    visibility: "public",
    title: matchId,
    displayName: legacyMeta.name || matchId,
    hasViewCode: Boolean(legacyMeta.viewCode),
    registryEntries: legacyPlayersToRegistryEntries(legacyPlayers, matchId, uid),
    roomMembers: [],
    teams: await tryImportLegacyTeams(matchId),
    history: await tryImportLegacyHistory(matchId),
    createdAt: timestamps.createdAt,
    updatedAt: timestamps.updatedAt,
  };
  await matchRepository.createMatch(matchId, matchPayload);

  try {
    await matchAccessRepository.createMatchAccess(matchId, {
      refCode: legacyMeta.refCode || "",
      viewCode: legacyMeta.viewCode || "",
    });
  } catch (e) {
    if (isPermissionDenied(e)) {
      throw permissionHintError(e);
    }
    throw e;
  }

  const live = await tryImportLegacyLive(matchId);
  if (live) {
    const existing = await liveMatchRepository.fetchLiveMatch(matchId);
    const doc = buildLiveMatchDocument(matchId, uid, live, existing, null);
    if (!existing) await liveMatchRepository.createLiveMatch(matchId, doc);
    else if (existing.ownerId === uid) await liveMatchRepository.updateLiveMatch(matchId, doc);
  }

  return matchRepository.fetchMatch(matchId);
}

async function ensureMatchReadable(matchId) {
  let match = await matchRepository.fetchMatch(matchId);
  if (match) {
    const migrated = await tryMigrateMatchCredentials(matchId, match);
    void ensureRoomSessionCompetition(matchId);
    return migrated;
  }
  const imported = await importLegacySession(matchId);
  if (imported) void ensureRoomSessionCompetition(matchId);
  return imported;
}

initMatchSubscriptionHub({
  ensureMatchReadable,
  subscribeMatch: (matchId, onData) =>
    matchRepository.subscribeMatch(matchId, (doc, meta) => {
      if (meta) reportSnapshotMetadata(matchId, "match", meta);
      setActiveSyncRoom(matchId);
      if (doc) onData(doc);
    }),
});

export async function getRoomMeta(matchId) {
  const match = await ensureMatchReadable(matchId);
  return matchMetaToLegacyShape(match);
}

/** Lightweight existence check (no legacy import). Archived rooms are free to reuse by the creator. */
export async function roomSessionDocumentExists(matchId) {
  try {
    const match = await matchRepository.fetchMatch(matchId);
    return Boolean(match && !match.archivedAt);
  } catch (e) {
    if (isPermissionDenied(e)) {
      return true;
    }
    throw e;
  }
}

/**
 * Allocates a unique session room id (Firestore `matches/{id}`).
 */
export async function allocateSessionRoomId(maxAttempts = 12) {
  const { generateSessionRoomId } = await import("../utils/matchRoomCode");
  for (let i = 0; i < maxAttempts; i += 1) {
    const id = generateSessionRoomId();
    // eslint-disable-next-line no-await-in-loop
    if (!(await roomSessionDocumentExists(id))) {
      return id;
    }
  }
  throw new Error("Could not allocate a unique room code");
}

/** Join verification without exposing credentials. */
export { verifyRoomJoin };

export async function saveRoomMeta(matchId, data) {
  const uid = ownerUid(data?.ownerUid);
  if (!uid) throw new Error("Authentication required to create a session");

  const viewCode = (data.viewCode || "").trim();
  const refCode = (data.refCode || "").trim();
  if (!refCode) throw new Error("Referee password required");
  const sportId = normalizeSportId(data?.sportId || DEFAULT_SPORT);

  const existing = await matchRepository.fetchMatch(matchId);
  if (existing) {
    if (existing.archivedAt) {
      if (existing.createdBy !== uid) {
        throw new Error("Session already exists");
      }
      await patchMatchFields(matchId, {
        archivedAt: deleteField(),
        displayName: data.name || matchId,
        sportId,
      });
      try {
        await matchAccessRepository.createMatchAccess(matchId, { refCode, viewCode });
      } catch (e) {
        if (isPermissionDenied(e)) {
          throw permissionHintError(e);
        }
        throw e;
      }
      return;
    }
    if (existing.createdBy !== uid) {
      throw new Error("Session already exists");
    }
    try {
      await matchAccessRepository.createMatchAccess(matchId, { refCode, viewCode });
      return;
    } catch (e) {
      if (isPermissionDenied(e)) {
        throw permissionHintError(e);
      }
      throw e;
    }
  }

  const timestamps = matchRepository.newMatchTimestamps();
  const matchPayload = {
    createdBy: uid,
    player1Id: PLACEHOLDER,
    player2Id: PLACEHOLDER,
    status: "pending",
    visibility: "public",
    title: matchId,
    displayName: data.name || matchId,
    hasViewCode: Boolean(viewCode),
    sportId,
    players: [],
    registryEntries: [],
    roomMembers: [],
    teams: [],
    history: [],
    createdAt: timestamps.createdAt,
    updatedAt: timestamps.updatedAt,
  };

  try {
    await batchCreateMatchWithAccess(matchId, matchPayload, { refCode, viewCode });
    trackRoomCreated({
      creatorPlays: data.creatorPlaysInSession !== false,
      isPrivate: Boolean(viewCode),
      roomLabelPresent: Boolean((data.name || "").trim()),
    });
    void ensureRoomSessionCompetition(matchId).then((competitionId) => {
      if (!competitionId) return;
      return syncRoomSessionGrants(matchId, { createdBy: uid });
    });
  } catch (e) {
    if (isPermissionDenied(e)) {
      throw permissionHintError(e);
    }
    throw e;
  }
}

function permissionHintError(cause) {
  const err = new Error(
    "You don't have permission to create or update this arena. Sign in with an account that has access, or ask the host."
  );
  err.cause = cause;
  err.code = "permission-denied";
  return err;
}

export async function claimDeputyReferee(matchId, refCodeInput) {
  const result = await verifyRoomJoin(matchId, refCodeInput);
  return result.ok && result.role === "referee";
}

export async function saveLiveMatch(roomCode, livePayload, options = {}) {
  const uid = ownerUid();
  if (!uid) return;

  const roomMatchId = options.roomMatchId ? String(options.roomMatchId).trim() : null;

  await ensureMatchReadable(roomCode);

  const match = await matchRepository.fetchMatch(roomCode);
  const isRoomOrganizer =
    match && (match.createdBy === uid || match.deputyRefereeUid === uid);
  if (!isRoomOrganizer) {
    if (!roomMatchId) return;
    try {
      const roomMatch = await fetchRoomMatch(roomCode, roomMatchId);
      if (!roomMatch || roomMatch.managedBy !== uid) return;
    } catch {
      return;
    }
  }

  if (isActivityRuntimePrimary(roomMatchId)) {
    await applyActivityLiveState(roomMatchId, roomCode, livePayload, uid);
    return;
  }

  const liveDocId = resolveLiveDocumentId(roomMatchId, roomCode);
  const existingLive = await liveMatchRepository.fetchLiveMatch(liveDocId);
  const liveDoc = buildLiveMatchDocument(
    roomCode,
    uid,
    livePayload,
    existingLive,
    roomMatchId
  );

  if (!existingLive) await liveMatchRepository.createLiveMatch(liveDocId, liveDoc);
  else await liveMatchRepository.updateLiveMatch(liveDocId, liveDoc);

  if (!roomMatchId) {
    const scoreA = Number.isInteger(livePayload?.scoreA) ? livePayload.scoreA : undefined;
    const scoreB = Number.isInteger(livePayload?.scoreB) ? livePayload.scoreB : undefined;

    await patchMatchFields(roomCode, {
      status: mapLiveStatusToMatchStatus(livePayload?.status),
      ...pickPlayerIds(livePayload),
      ...(scoreA !== undefined ? { scorePlayer1: scoreA } : {}),
      ...(scoreB !== undefined ? { scorePlayer2: scoreB } : {}),
    });
  }
}

export async function saveRegistryState(matchId, registryEntries, roomMembers) {
  const result = await persistRegistryState(matchId, { registryEntries, roomMembers });
  if (result?.registryEntries) {
    void syncRegistryToParticipants(matchId, result.registryEntries);
  }
  return result;
}

export async function saveTeams(matchId, teams) {
  const uid = ownerUid();
  const match = await matchRepository.fetchMatch(matchId);
  if (!match || (match.createdBy !== uid && match.deputyRefereeUid !== uid)) return;
  await patchMatchFields(matchId, { teams });
}

export async function saveHistory(matchId, history) {
  const uid = ownerUid();
  try {
    const match = await matchRepository.fetchMatch(matchId);
    if (!match || (match.createdBy !== uid && match.deputyRefereeUid !== uid)) return;
    await patchMatchFields(matchId, { history });
  } catch (err) {
    if (isPermissionDenied(err)) return;
    console.warn("[session] saveHistory failed", err);
  }
}

function stripLiveInternalFields(data) {
  if (!data) return data;
  const { matchId, roomMatchId, ownerId, createdAt, updatedAt, ...rest } = data;
  return rest;
}

/**
 * @param {string} roomCode Parent session / room code
 * @param {(data: object | null) => void} onData
 * @param {{ roomMatchId?: string | null }} [options]
 */
export function subscribeLiveMatch(roomCode, onData, options = {}) {
  const roomMatchId = options.roomMatchId ? String(options.roomMatchId).trim() : null;
  setActiveSyncRoom(roomCode);

  if (isActivityRuntimePrimary(roomMatchId)) {
    return subscribeActivityRuntime(roomMatchId, (payload) => {
      reportSnapshotMetadata(roomMatchId, "activity", {});
      onData(payload);
    });
  }

  const liveDocId = resolveLiveDocumentId(roomMatchId, roomCode);
  let legacyTried = false;
  let cancelled = false;

  const deliver = (data, meta) => {
    if (cancelled) return;
    // Local persistence often emits a cache miss before the server doc.
    // That is not an idle court — applying it wipes a spectator refresh.
    if (!data && meta?.fromCache) return;
    onData(data);
  };

  const pull = async () => {
    let live = await liveMatchRepository.fetchLiveMatch(liveDocId);
    if (cancelled) return;
    if (!live && !legacyTried && !roomMatchId) {
      legacyTried = true;
      await importLegacySession(roomCode);
      if (cancelled) return;
      live = await liveMatchRepository.fetchLiveMatch(liveDocId);
    }
    if (cancelled) return;
    if (live) deliver(stripLiveInternalFields(live));
    else deliver(null);
  };

  pull();
  const unsubscribe = liveMatchRepository.subscribeLiveMatchDoc(liveDocId, (doc, meta) => {
    if (meta) reportSnapshotMetadata(liveDocId, "live", meta);
    if (doc) deliver(stripLiveInternalFields(doc), meta);
    else deliver(null, meta);
  });

  return () => {
    cancelled = true;
    unsubscribe();
  };
}

export function subscribePlayers(matchId, onRegistryEntries) {
  return subscribeToMatchDocument(matchId, {
    onRegistry: (entries) => onRegistryEntries(entries),
  });
}

export function subscribeTeams(matchId, onTeams) {
  return subscribeToMatchDocument(matchId, {
    onTeams: (teams) => onTeams(teams),
  });
}

export function subscribeHistory(matchId, onHistory) {
  return subscribeToMatchDocument(matchId, {
    onHistory: (history) => onHistory(history),
  });
}

/** Alias for match document watch (ownership / permissions). */
export function watchSessionMatch(matchId, onData) {
  return subscribeToMatchDocument(matchId, {
    onMatch: (doc) => onData(doc),
  });
}

/** Coordinated match document subscription (single onSnapshot). */
export { subscribeToMatchDocument };

export async function fetchSessionMatch(matchId) {
  return ensureMatchReadable(matchId);
}
